import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getOrders, getQRScans } from '../api';
import { getRestaurants } from '../restaurantsApi';
import {
  clearAdminSession,
  adminListRestaurants,
  adminRegenerateCode,
  adminRevokeSessions,
  adminSetRestaurantPassword,
  adminIssuePairingCode,
} from '../menuApi';
import { getWallet, requestCashout, startOnboarding, inviteRestaurantOwner } from '../walletApi';
import { getHotels } from '../hotels';

/* ── Constants ───────────────────────────────────────────────────────────── */
// Hotels come from the shared definition — this file used to carry its own copy,
// which had drifted to "Pinnacle Holiday" while every other screen said
// "Pinnacle Holiday Lodge".
const GAPGO_HOTELS = getHotels();

const REDSPEED_PRODUCTS = [
  { id: 'gapgo',    name: 'GapGo',       desc: 'Cloud Guest Services',  emoji: '🏨', status: 'live',    accent: '#F59E0B' },
  { id: 'telecom',  name: 'RS Telecom',  desc: 'Mobile & Data Plans',   emoji: '📡', status: 'coming',  accent: '#38BDF8' },
  { id: 'field',    name: 'RS Field',    desc: 'Field Service Ops',     emoji: '🔧', status: 'coming',  accent: '#4ADE80' },
  { id: 'insights', name: 'RS Insights', desc: 'Business Intelligence', emoji: '📊', status: 'coming',  accent: '#A78BFA' },
];

const STATUS_META = {
  new:       { label: 'New',       color: '#F59E0B', bg: 'rgba(245,158,11,0.13)' },
  preparing: { label: 'Preparing', color: '#38BDF8', bg: 'rgba(56,189,248,0.13)' },
  ready:     { label: 'Ready',     color: '#4ADE80', bg: 'rgba(74,222,128,0.13)' },
  complete:  { label: 'Complete',  color: '#6B7280', bg: 'rgba(107,114,128,0.1)' },
  completed: { label: 'Complete',  color: '#6B7280', bg: 'rgba(107,114,128,0.1)' },
  rejected:  { label: 'Rejected',  color: '#EF4444', bg: 'rgba(239,68,68,0.12)' },
  refunded:  { label: 'Refunded',  color: '#EF4444', bg: 'rgba(239,68,68,0.12)' },
};

const HOTEL_MAP = Object.fromEntries(GAPGO_HOTELS.map(h => [h.id, h]));

/* ── Helpers ─────────────────────────────────────────────────────────────── */
const today = () => new Date().toISOString().slice(0, 10);

const isToday = (ts) => ts && (ts.slice(0, 10) === today());

const fmt$ = (n) => '$' + (n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const fmtTime = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  const diff = (Date.now() - d) / 1000;
  if (diff < 60)   return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true });
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
};

const fmtItems = (items) => {
  if (!items) return '—';
  if (typeof items === 'string') return items;
  if (Array.isArray(items)) return items.map(i => `${i.name}${i.qty > 1 ? ` ×${i.qty}` : ''}`).join(', ') || '—';
  return '—';
};

const parseDevice = (ua) => {
  if (!ua || typeof ua !== 'string') return 'Unknown';
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Mac/.test(ua)) return 'Mac';
  if (/Win/.test(ua)) return 'Windows';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Device';
};

/* ── Main Component ──────────────────────────────────────────────────────── */
export default function AdminPanel() {
  const navigate = useNavigate();
  const [activeProduct, setActiveProduct] = useState('gapgo');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [clock, setClock] = useState(new Date());
  const [sideOpen, setSideOpen] = useState(true);
  const [scans, setScans] = useState([]);
  const [scansLoading, setScansLoading] = useState(true);
  const [restaurants, setRestaurants] = useState([]);
  const [wallets, setWallets] = useState({}); // restaurantId -> { balance, transactions } | { error }
  const [walletsLoading, setWalletsLoading] = useState(true);
  const [walletBusy, setWalletBusy] = useState(null); // restaurantId currently onboarding/cashing out
  const [inviteFor, setInviteFor] = useState(null);   // restaurantId whose invite form is open
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteResult, setInviteResult] = useState(null); // { email, temporaryPassword }

  // Restaurant access codes. Separate from the wallet list above because that one
  // reads the `restaurants` Lambda, which is not deployed — this reads the `menu`
  // Lambda, which is, so this section works today.
  const [access, setAccess] = useState([]);
  const [accessLoading, setAccessLoading] = useState(true);
  const [accessError, setAccessError] = useState('');
  const [accessBusy, setAccessBusy] = useState(null);  // restaurantId mid-action
  const [newCode, setNewCode] = useState(null);        // { restaurantId, name, accessCode }
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [pwFor, setPwFor] = useState(null);            // restaurant whose password is being set
  const [pwValue, setPwValue] = useState('');
  const [pairingCode, setPairingCode] = useState(null); // { restaurantId, code }, shown once

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const loadData = useCallback(async () => {
    try {
      const data = await getOrders();
      setOrders(Array.isArray(data) ? data : []);
      setLastRefresh(new Date());
    } catch (e) {
      console.error('Admin load error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const t = setInterval(loadData, 30000);
    return () => clearInterval(t);
  }, [loadData]);

  const loadScans = useCallback(async () => {
    try {
      const data = await getQRScans();
      setScans(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Admin scans load error:', e);
    } finally {
      setScansLoading(false);
    }
  }, []);

  useEffect(() => {
    loadScans();
    const t = setInterval(loadScans, 30000);
    return () => clearInterval(t);
  }, [loadScans]);

  const loadAccess = useCallback(async () => {
    try {
      setAccessError('');
      setAccess(await adminListRestaurants());
    } catch (e) {
      setAccessError((e && e.message) || 'Could not load restaurants');
    } finally {
      setAccessLoading(false);
    }
  }, []);

  useEffect(() => { loadAccess(); }, [loadAccess]);

  const handleRegenerate = async (r) => {
    setAccessBusy(r.restaurantId);
    setAccessError('');
    try {
      const res = await adminRegenerateCode(r.restaurantId);
      // Shown ONCE — only the hash is stored, so this is the only chance to copy it.
      setNewCode({ restaurantId: r.restaurantId, name: r.name, accessCode: res.accessCode });
      await loadAccess();
    } catch (e) {
      setAccessError((e && e.message) || 'Could not regenerate the code');
    } finally {
      setAccessBusy(null);
    }
  };

  const handleSetPassword = async (r) => {
    if (!pwValue || pwValue.length < 4) {
      setAccessError('Password must be at least 4 characters');
      return;
    }
    setAccessBusy(r.restaurantId);
    setAccessError('');
    try {
      await adminSetRestaurantPassword(r.restaurantId, pwValue);
      setPwFor(null);
      setPwValue('');
      await loadAccess();
    } catch (e) {
      setAccessError((e && e.message) || 'Could not set the password');
    } finally {
      setAccessBusy(null);
    }
  };

  const handleIssuePairing = async (r) => {
    setAccessBusy(r.restaurantId);
    setAccessError('');
    try {
      const res = await adminIssuePairingCode(r.restaurantId);
      // Shown once, same as an access code — only the hash is stored.
      setPairingCode({ restaurantId: r.restaurantId, code: res.code });
    } catch (e) {
      setAccessError((e && e.message) || 'Could not issue a pairing code');
    } finally {
      setAccessBusy(null);
    }
  };

  const handleRevoke = async (r) => {
    setAccessBusy(r.restaurantId);
    setAccessError('');
    try {
      await adminRevokeSessions(r.restaurantId);
      setConfirmRevoke(null);
      await loadAccess();
    } catch (e) {
      setAccessError((e && e.message) || 'Could not revoke sessions');
    } finally {
      setAccessBusy(null);
    }
  };

  const loadWallets = useCallback(async () => {
    try {
      const list = await getRestaurants();
      setRestaurants(list);
      const entries = await Promise.all(list.map(async (r) => {
        try {
          const data = await getWallet(r.restaurantId);
          return [r.restaurantId, data];
        } catch (e) {
          return [r.restaurantId, { error: true }];
        }
      }));
      setWallets(Object.fromEntries(entries));
    } catch (e) {
      console.error('Admin wallets load error:', e);
    } finally {
      setWalletsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWallets();
    const t = setInterval(loadWallets, 60000);
    return () => clearInterval(t);
  }, [loadWallets]);

  const handleOnboard = async (restaurantId) => {
    setWalletBusy(restaurantId);
    try {
      const { url } = await startOnboarding(restaurantId);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      alert(e.message || 'Could not start Stripe onboarding');
    } finally {
      setWalletBusy(null);
    }
  };

  const handleCashout = async (restaurantId, available) => {
    if (!window.confirm(`Cash out $${available.toFixed(2)} to ${restaurantId}'s bank account via Stripe?`)) return;
    setWalletBusy(restaurantId);
    try {
      await requestCashout(restaurantId);
      await loadWallets();
    } catch (e) {
      alert(e.message || 'Cashout failed');
    } finally {
      setWalletBusy(null);
    }
  };

  const handleInvite = async (restaurantId) => {
    if (!inviteEmail.trim()) return;
    setWalletBusy(restaurantId);
    try {
      const res = await inviteRestaurantOwner({ email: inviteEmail.trim(), restaurantId, invitedBy: 'admin-panel' });
      // Shown once, right here — the temp password is never stored or re-retrievable.
      setInviteResult({ email: res.email, temporaryPassword: res.temporaryPassword });
      setInviteEmail('');
      setInviteFor(null);
    } catch (e) {
      alert(e.message || 'Could not create the partner login');
    } finally {
      setWalletBusy(null);
    }
  };

  const handleLogout = () => {
    // Clears the signed admin token, not just the retired `adminAuth` flag —
    // otherwise "sign out" left a valid session sitting in localStorage.
    clearAdminSession();
    navigate('/admin-login');
  };

  /* ── Derived stats ── */
  const todayOrders   = orders.filter(o => isToday(o.createdAt));
  const activeOrders  = orders.filter(o => ['new', 'preparing', 'ready'].includes(o.status));
  const todayRevenue  = todayOrders
    .filter(o => !['rejected', 'refunded'].includes(o.status))
    .reduce((s, o) => s + (parseFloat(o.total) || 0), 0);

  const hotelStats = GAPGO_HOTELS.map(h => {
    const hToday  = todayOrders.filter(o => o.hotelId === h.id);
    const hActive = activeOrders.filter(o => o.hotelId === h.id);
    const hRev    = hToday.filter(o => !['rejected', 'refunded'].includes(o.status))
                          .reduce((s, o) => s + (parseFloat(o.total) || 0), 0);
    const last    = [...hToday].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))[0];
    return { ...h, todayCount: hToday.length, activeCount: hActive.length, revenue: hRev, lastOrder: last };
  });
  const maxTodayOrders = Math.max(...hotelStats.map(h => h.todayCount), 1);

  const feedOrders = [...orders]
    .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))
    .slice(0, 30);

  const clockStr = clock.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

  return (
    <div style={S.shell}>
      <div style={S.grain} />

      {/* ── Sidebar ── */}
      <aside style={{ ...S.sidebar, width: sideOpen ? '220px' : '64px', transition: 'width 0.25s ease' }}>
        {/* Brand */}
        <div style={S.sideTop}>
          <motion.span
            animate={{ scale: [1, 1.6, 1], opacity: [1, 0.4, 1] }}
            transition={{ duration: 2.8, repeat: Infinity }}
            style={S.sideDot}
          />
          {sideOpen && <span style={S.sideBrand}>GapGo</span>}
          <button style={S.collapseBtn} onClick={() => setSideOpen(o => !o)}>
            {sideOpen ? '←' : '→'}
          </button>
        </div>

        <div style={S.sideLabel}>{sideOpen && 'PRODUCTS'}</div>

        {REDSPEED_PRODUCTS.map(p => (
          <button
            key={p.id}
            style={{
              ...S.sideItem,
              background: activeProduct === p.id ? 'rgba(255,255,255,0.08)' : 'transparent',
              border: activeProduct === p.id ? `1px solid rgba(255,255,255,0.12)` : '1px solid transparent',
              opacity: p.status === 'coming' ? 0.45 : 1,
              cursor: p.status === 'coming' ? 'default' : 'pointer',
              justifyContent: sideOpen ? 'flex-start' : 'center',
            }}
            onClick={() => p.status === 'live' && setActiveProduct(p.id)}
          >
            <span style={S.sideEmoji}>{p.emoji}</span>
            {sideOpen && (
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div style={S.sideItemName}>{p.name}</div>
                {p.status === 'coming' && <div style={S.sideItemSub}>Coming soon</div>}
              </div>
            )}
            {sideOpen && activeProduct === p.id && (
              <div style={{ ...S.sideActiveDot, background: p.accent }} />
            )}
          </button>
        ))}

        <div style={S.sideBottom}>
          <button style={S.logoutBtn} onClick={handleLogout}>
            <span>⏏</span>
            {sideOpen && <span>Sign out</span>}
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <div style={S.main}>

        {/* ── Top nav ── */}
        <div style={S.topNav}>
          <div>
            <div style={S.navTitle}>Redspeed Command Center</div>
            <div style={S.navSub}>Super Admin · GapGo Module</div>
          </div>
          <div style={S.navRight}>
            <div style={S.clockPill}>
              <motion.span
                animate={{ opacity: [1, 0.3, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
                style={S.liveDot}
              />
              {clockStr}
            </div>
            <button
              style={{ ...S.refreshBtn, opacity: loading ? 0.5 : 1 }}
              onClick={loadData}
              disabled={loading}
            >
              <motion.span
                animate={{ rotate: loading ? 360 : 0 }}
                transition={{ duration: 0.8, repeat: loading ? Infinity : 0, ease: 'linear' }}
              >
                ↻
              </motion.span>
              {lastRefresh && <span style={S.refreshTime}>{fmtTime(lastRefresh)}</span>}
            </button>
          </div>
        </div>

        <div style={S.content}>

          {/* ── KPI row ── */}
          <div style={S.kpiRow}>
            {[
              { label: 'Businesses',    value: GAPGO_HOTELS.length, unit: 'onboarded',  accent: '#A78BFA', emoji: '🏢' },
              { label: 'Orders Today',  value: todayOrders.length,  unit: 'placed',     accent: '#F59E0B', emoji: '📦' },
              { label: 'Revenue Today', value: fmt$(todayRevenue),  unit: 'collected',  accent: '#4ADE80', emoji: '💰' },
              { label: 'Active Now',    value: activeOrders.length, unit: 'in progress',accent: '#38BDF8', emoji: '🔴' },
            ].map((k, i) => (
              <motion.div
                key={k.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.07, type: 'spring', stiffness: 200 }}
                style={{ ...S.kpiCard, borderColor: `${k.accent}22` }}
              >
                <div style={{ ...S.kpiAccentLine, background: k.accent }} />
                <div style={S.kpiEmoji}>{k.emoji}</div>
                <div style={{ ...S.kpiValue, color: k.accent }}>{k.value}</div>
                <div style={S.kpiLabel}>{k.label}</div>
                <div style={S.kpiUnit}>{k.unit}</div>
              </motion.div>
            ))}
          </div>

          {/* ── Section: Hotels ── */}
          <div style={S.section}>
            <div style={S.sectionHead}>
              <span style={S.sectionTitle}>Onboarded Businesses</span>
              <span style={S.sectionCount}>{GAPGO_HOTELS.length} active</span>
            </div>
            <div style={S.hotelGrid}>
              {hotelStats.map((h, i) => (
                <motion.div
                  key={h.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.08, type: 'spring', stiffness: 180 }}
                  style={S.hotelCard}
                >
                  {/* Hotel color strip */}
                  <div style={{ ...S.hotelStrip, background: h.accent }} />

                  <div style={S.hotelHead}>
                    <div style={{ ...S.hotelAvatar, background: `${h.accent}20`, color: h.accent, border: `1px solid ${h.accent}40` }}>
                      {h.shortName}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={S.hotelName}>{h.name}</div>
                      <div style={S.hotelMeta}>{h.rooms} rooms</div>
                    </div>
                    <div style={{ ...S.statusPip, background: h.activeCount > 0 ? '#4ADE80' : '#374151', boxShadow: h.activeCount > 0 ? '0 0 8px #4ADE8088' : 'none' }} />
                  </div>

                  <div style={S.hotelStats}>
                    <div style={S.hotelStat}>
                      <div style={{ ...S.hotelStatVal, color: h.accent }}>{h.todayCount}</div>
                      <div style={S.hotelStatLabel}>Today</div>
                    </div>
                    <div style={S.hotelStatDivider} />
                    <div style={S.hotelStat}>
                      <div style={{ ...S.hotelStatVal, color: '#4ADE80' }}>{fmt$(h.revenue)}</div>
                      <div style={S.hotelStatLabel}>Revenue</div>
                    </div>
                    <div style={S.hotelStatDivider} />
                    <div style={S.hotelStat}>
                      <div style={{ ...S.hotelStatVal, color: h.activeCount > 0 ? '#F59E0B' : '#374151' }}>{h.activeCount}</div>
                      <div style={S.hotelStatLabel}>Active</div>
                    </div>
                  </div>

                  {/* Activity bar */}
                  <div style={S.barTrack}>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(h.todayCount / maxTodayOrders) * 100}%` }}
                      transition={{ delay: 0.4 + i * 0.08, duration: 0.8, ease: 'easeOut' }}
                      style={{ ...S.barFill, background: h.accent }}
                    />
                  </div>

                  <div style={S.hotelLast}>
                    {h.lastOrder
                      ? `Last order ${fmtTime(h.lastOrder.createdAt)}`
                      : 'No orders today'}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* ── Section: Restaurant Access ──
              The answer to "a restaurant lost its code and nobody can help them".
              Reads the `menu` Lambda (deployed) rather than `restaurants` (not),
              so unlike the wallets section below this one works today. */}
          <div style={S.section}>
            <div style={S.sectionHead}>
              <span style={S.sectionTitle}>Restaurant Access</span>
              <span style={S.sectionCount}>
                {access.length} restaurant{access.length === 1 ? '' : 's'} · each has its own sign-in code
              </span>
            </div>

            {accessError && <div style={S.accessError}>⚠ {accessError}</div>}

            {/* The new code, shown exactly once. */}
            {newCode && (
              <div style={S.codeReveal}>
                <div style={S.codeRevealLabel}>
                  NEW ACCESS CODE FOR {(newCode.name || newCode.restaurantId).toUpperCase()}
                </div>
                <div style={S.codeRevealValue}>{newCode.accessCode}</div>
                <div style={S.codeRevealNote}>
                  Copy this now and give it to the restaurant. Only its hash is stored, so it
                  cannot be shown again — if you lose it, generate another. Everyone signed in
                  with the old code has been signed out.
                </div>
                <button style={S.codeRevealBtn} onClick={() => setNewCode(null)}>
                  I have copied it
                </button>
              </div>
            )}

            {accessLoading ? (
              <div style={S.emptyState}>Loading restaurants…</div>
            ) : access.length === 0 ? (
              <div style={S.emptyState}>No restaurants yet</div>
            ) : (
              <div style={S.hotelGrid}>
                {access.map((r) => {
                  const busy = accessBusy === r.restaurantId;
                  return (
                    <div key={r.restaurantId} style={S.accessCard}>
                      <div style={S.accessName}>{r.name || r.restaurantId}</div>
                      <div style={S.accessMeta}>
                        {r.hasAccessCode ? (
                          <>Code set {r.accessCodeSetAt ? new Date(r.accessCodeSetAt).toLocaleDateString() : ''} · v{r.accessCodeVersion}</>
                        ) : (
                          <span style={{ color: '#F59E0B' }}>No code yet — generate one</span>
                        )}
                      </div>
                      <div style={S.accessMetaDim}>
                        {r.ownerEmail
                          ? `Owner: ${r.ownerEmail}`
                          : 'No owner email on file — they cannot reset it themselves'}
                      </div>

                      <div style={S.accessBtnRow}>
                        <button
                          style={{ ...S.accessBtnPrimary, opacity: busy ? 0.5 : 1 }}
                          disabled={busy}
                          onClick={() => handleRegenerate(r)}
                        >
                          {busy ? 'Working…' : r.hasAccessCode ? 'New code' : 'Generate code'}
                        </button>
                        {/* The backstop when a restaurant cannot use the self-serve
                            reset — usually because there is no owner email on file for
                            a code to be sent to. `adminSetRestaurantPassword` has
                            existed in the Lambda all along with no UI, which is why
                            the only way to provision a password was a hand-crafted
                            API call or a direct DynamoDB write. */}
                        <button
                          style={{ ...S.accessBtnGhost, opacity: busy ? 0.5 : 1 }}
                          disabled={busy}
                          onClick={() => { setPwFor(r); setPwValue(''); }}
                        >
                          {r.hasPassword ? 'Reset password' : 'Set password'}
                        </button>
                        <button
                          style={{ ...S.accessBtnGhost, opacity: busy ? 0.5 : 1 }}
                          disabled={busy}
                          onClick={() => handleIssuePairing(r)}
                        >
                          Pairing code
                        </button>
                        {r.hasAccessCode && (
                          confirmRevoke === r.restaurantId ? (
                            <button
                              style={{ ...S.accessBtnDanger, opacity: busy ? 0.5 : 1 }}
                              disabled={busy}
                              onClick={() => handleRevoke(r)}
                            >
                              Confirm sign-out
                            </button>
                          ) : (
                            <button
                              style={S.accessBtnGhost}
                              onClick={() => setConfirmRevoke(r.restaurantId)}
                            >
                              Sign out all devices
                            </button>
                          )
                        )}
                      </div>

                      {pwFor && pwFor.restaurantId === r.restaurantId && (
                        <div style={S.accessBtnRow}>
                          <input
                            type="text"
                            autoFocus
                            placeholder="New password (min 4 characters)"
                            value={pwValue}
                            onChange={(e) => setPwValue(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSetPassword(r)}
                            style={S.accessInput}
                          />
                          <button style={S.accessBtnPrimary} disabled={busy} onClick={() => handleSetPassword(r)}>
                            {busy ? 'Saving…' : 'Save'}
                          </button>
                          <button style={S.accessBtnGhost} onClick={() => setPwFor(null)}>Cancel</button>
                        </div>
                      )}

                      {pairingCode && pairingCode.restaurantId === r.restaurantId && (
                        <div style={S.accessMeta}>
                          Pairing code <strong style={{ fontSize: 18, letterSpacing: 3 }}>{pairingCode.code}</strong>
                          {' — '}read it to them, it expires in 30 minutes and works once.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Section: Restaurant Wallets ── */}
          <div style={S.section}>
            <div style={S.sectionHead}>
              <span style={S.sectionTitle}>Restaurant Wallets</span>
              <span style={S.sectionCount}>{restaurants.length} restaurant{restaurants.length === 1 ? '' : 's'} · 15% platform commission</span>
            </div>

            {walletsLoading && restaurants.length === 0 ? (
              <div style={S.emptyState}>Loading wallets…</div>
            ) : restaurants.length === 0 ? (
              <div style={S.emptyState}>No restaurants onboarded yet</div>
            ) : (
              <div style={S.hotelGrid}>
                {restaurants.map((r, i) => {
                  const w = wallets[r.restaurantId];
                  const busy = walletBusy === r.restaurantId;
                  const onboarded = w && !w.error && w.balance !== undefined;
                  const payoutsReady = r.payoutsEnabled;
                  const available = onboarded ? w.balance.available : 0;
                  return (
                    <motion.div
                      key={r.restaurantId}
                      initial={{ opacity: 0, y: 24 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + i * 0.08, type: 'spring', stiffness: 180 }}
                      style={S.hotelCard}
                    >
                      <div style={{ ...S.hotelStrip, background: r.brandColor || '#E8001D' }} />
                      <div style={S.hotelHead}>
                        <div style={{ ...S.hotelAvatar, background: `${r.brandColor || '#E8001D'}20`, color: r.brandColor || '#E8001D', border: `1px solid ${r.brandColor || '#E8001D'}40` }}>
                          {r.emoji || '🍽️'}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={S.hotelName}>{r.name}</div>
                          <div style={S.hotelMeta}>{payoutsReady ? 'Payouts enabled' : 'Onboarding required'}</div>
                        </div>
                        <div style={{ ...S.statusPip, background: payoutsReady ? '#4ADE80' : '#F59E0B', boxShadow: payoutsReady ? '0 0 8px #4ADE8088' : '0 0 8px #F59E0B88' }} />
                      </div>

                      <div style={S.hotelStats}>
                        <div style={S.hotelStat}>
                          <div style={{ ...S.hotelStatVal, color: '#4ADE80' }}>{fmt$(onboarded ? w.balance.totalEarned : 0)}</div>
                          <div style={S.hotelStatLabel}>Earned</div>
                        </div>
                        <div style={S.hotelStatDivider} />
                        <div style={S.hotelStat}>
                          <div style={{ ...S.hotelStatVal, color: '#6B7280' }}>{fmt$(onboarded ? w.balance.totalPaidOut : 0)}</div>
                          <div style={S.hotelStatLabel}>Paid Out</div>
                        </div>
                        <div style={S.hotelStatDivider} />
                        <div style={S.hotelStat}>
                          <div style={{ ...S.hotelStatVal, color: '#F59E0B' }}>{fmt$(available)}</div>
                          <div style={S.hotelStatLabel}>Available</div>
                        </div>
                      </div>

                      {payoutsReady ? (
                        <button
                          style={{ ...S.walletBtn, opacity: busy || available <= 0 ? 0.5 : 1 }}
                          disabled={busy || available <= 0}
                          onClick={() => handleCashout(r.restaurantId, available)}
                        >
                          {busy ? 'Processing…' : `Cash out $${available.toFixed(2)}`}
                        </button>
                      ) : (
                        <button
                          style={{ ...S.walletBtn, opacity: busy ? 0.5 : 1, background: 'rgba(245,158,11,0.12)', color: '#F59E0B' }}
                          disabled={busy}
                          onClick={() => handleOnboard(r.restaurantId)}
                        >
                          {busy ? 'Opening Stripe…' : 'Complete Stripe onboarding'}
                        </button>
                      )}

                      {/* Partner portal access — creates the owner's own login so they
                          can see earnings and cash out without staff doing it for them. */}
                      {inviteFor === r.restaurantId ? (
                        <div style={S.inviteBox}>
                          <input
                            style={S.inviteInput}
                            type="email"
                            placeholder="owner@restaurant.com.au"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            autoFocus
                          />
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              style={{ ...S.walletBtn, marginTop: 0, opacity: busy ? 0.5 : 1 }}
                              disabled={busy}
                              onClick={() => handleInvite(r.restaurantId)}
                            >
                              {busy ? 'Creating…' : 'Create login'}
                            </button>
                            <button
                              style={{ ...S.walletBtn, marginTop: 0, background: 'rgba(255,255,255,0.05)', color: '#9CA3AF' }}
                              onClick={() => { setInviteFor(null); setInviteEmail(''); }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          style={{ ...S.walletBtn, background: 'rgba(255,255,255,0.05)', color: '#9CA3AF' }}
                          onClick={() => { setInviteFor(r.restaurantId); setInviteResult(null); }}
                        >
                          Invite owner to partner portal
                        </button>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}

            {inviteResult && (
              <div style={S.inviteResult}>
                <div style={S.inviteResultTitle}>Partner login created</div>
                <div style={S.inviteResultBody}>
                  Give these to <strong>{inviteResult.email}</strong> directly — the temporary
                  password is shown once and can't be retrieved again. They'll set their own
                  password at <strong>/partner/login</strong>.
                </div>
                <div style={S.inviteResultPass}>{inviteResult.temporaryPassword}</div>
                <button style={S.inviteResultDismiss} onClick={() => setInviteResult(null)}>Done</button>
              </div>
            )}
          </div>

          {/* ── Section: Live Activity Feed ── */}
          <div style={S.section}>
            <div style={S.sectionHead}>
              <span style={S.sectionTitle}>Live Activity</span>
              <span style={S.sectionCount}>All hotels · last 30 orders</span>
            </div>

            {loading && orders.length === 0 ? (
              <div style={S.emptyState}>Loading orders…</div>
            ) : feedOrders.length === 0 ? (
              <div style={S.emptyState}>No orders yet today</div>
            ) : (
              <div style={S.feedTable}>
                {/* Table header */}
                <div style={S.feedHeader}>
                  {['Time', 'Business', 'Room', 'Items', 'Amount', 'Status'].map(h => (
                    <div key={h} style={S.feedHeaderCell}>{h}</div>
                  ))}
                </div>
                {/* Rows */}
                <AnimatePresence initial={false}>
                  {feedOrders.map((o, i) => {
                    const hotel  = HOTEL_MAP[o.hotelId];
                    const status = STATUS_META[o.status] || STATUS_META.new;
                    return (
                      <motion.div
                        key={o.orderId || i}
                        initial={{ opacity: 0, x: -12 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ delay: i * 0.025 }}
                        style={S.feedRow}
                      >
                        <div style={S.feedCell}>
                          <span style={S.feedTime}>{fmtTime(o.createdAt)}</span>
                        </div>
                        <div style={S.feedCell}>
                          {hotel ? (
                            <span style={{ ...S.hotelBadge, color: hotel.accent, borderColor: `${hotel.accent}40`, background: `${hotel.accent}0f` }}>
                              {hotel.shortName}
                            </span>
                          ) : (
                            <span style={S.feedMuted}>{o.hotelId || '—'}</span>
                          )}
                        </div>
                        <div style={S.feedCell}>
                          <span style={S.feedRoom}>Room {o.roomNumber || '—'}</span>
                        </div>
                        <div style={{ ...S.feedCell, flex: 2 }}>
                          <span style={S.feedItems} title={fmtItems(o.items)}>
                            {fmtItems(o.items)}
                          </span>
                        </div>
                        <div style={S.feedCell}>
                          <span style={S.feedAmount}>{fmt$(parseFloat(o.total))}</span>
                        </div>
                        <div style={S.feedCell}>
                          <span style={{ ...S.statusBadge, color: status.color, background: status.bg }}>
                            {status.label}
                          </span>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            )}
          </div>

          {/* ── Section: QR Scan Analytics ── */}
          <div style={S.section}>
            <div style={S.sectionHead}>
              <span style={S.sectionTitle}>QR Scan Analytics</span>
              <span style={S.sectionCount}>All hotels · last {scans.length} scans</span>
            </div>

            {scansLoading && scans.length === 0 ? (
              <div style={S.emptyState}>Loading scans…</div>
            ) : scans.length === 0 ? (
              <div style={S.emptyState}>No scans recorded yet</div>
            ) : (
              <div style={S.feedTable}>
                <div style={S.feedHeader}>
                  {['Time', 'Business', 'Room', 'Device', 'Page'].map(h => (
                    <div key={h} style={S.feedHeaderCell}>{h}</div>
                  ))}
                </div>
                <AnimatePresence initial={false}>
                  {scans.slice(0, 30).map((s, i) => {
                    const hotel = HOTEL_MAP[s.hotelId];
                    const device = parseDevice(s.userAgent);
                    return (
                      <motion.div
                        key={s.scanId || i}
                        initial={{ opacity: 0, x: -12 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ delay: i * 0.025 }}
                        style={S.feedRow}
                      >
                        <div style={S.feedCell}>
                          <span style={S.feedTime}>{fmtTime(s.scannedAt)}</span>
                        </div>
                        <div style={S.feedCell}>
                          {hotel ? (
                            <span style={{ ...S.hotelBadge, color: hotel.accent, borderColor: `${hotel.accent}40`, background: `${hotel.accent}0f` }}>
                              {hotel.shortName}
                            </span>
                          ) : (
                            <span style={S.feedMuted}>{s.hotelId || '—'}</span>
                          )}
                        </div>
                        <div style={S.feedCell}>
                          <span style={S.feedRoom}>Room {s.roomNumber || '—'}</span>
                        </div>
                        <div style={S.feedCell}>
                          <span style={{ ...S.feedItems, maxWidth: '160px' }} title={device}>
                            {device}
                          </span>
                        </div>
                        <div style={S.feedCell}>
                          <span style={S.feedMuted}>{s.url ? new URL(s.url).pathname : '—'}</span>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            )}
          </div>

          {/* ── Section: Other Redspeed Products (preview) ── */}
          <div style={S.section}>
            <div style={S.sectionHead}>
              <span style={S.sectionTitle}>Redspeed Product Suite</span>
              <span style={S.sectionCount}>Expanding</span>
            </div>
            <div style={S.productGrid}>
              {REDSPEED_PRODUCTS.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.06 }}
                  style={{
                    ...S.productCard,
                    border: p.status === 'live'
                      ? `1px solid ${p.accent}40`
                      : '1px solid rgba(255,255,255,0.06)',
                    opacity: p.status === 'coming' ? 0.55 : 1,
                  }}
                >
                  <span style={S.productEmoji}>{p.emoji}</span>
                  <div style={S.productName}>{p.name}</div>
                  <div style={S.productDesc}>{p.desc}</div>
                  <div style={{
                    ...S.productStatus,
                    color: p.status === 'live' ? p.accent : '#374151',
                    background: p.status === 'live' ? `${p.accent}15` : 'rgba(255,255,255,0.04)',
                  }}>
                    {p.status === 'live' ? '● Live' : 'Coming soon'}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

/* ── Styles ───────────────────────────────────────────────────────────────── */
const S = {
  shell: {
    minHeight: '100vh',
    background: '#050507',
    display: 'flex',
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif',
    position: 'relative',
    overflow: 'hidden',
  },
  grain: {
    position: 'fixed', inset: 0, opacity: 0.022, pointerEvents: 'none', zIndex: 0,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'repeat', backgroundSize: '180px',
  },

  /* ── Sidebar ── */
  sidebar: {
    flexShrink: 0,
    background: 'rgba(255,255,255,0.025)',
    borderRight: '1px solid rgba(255,255,255,0.07)',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 10,
    height: '100vh',
    // Was declared twice in this object — `relative` then `sticky`. The later key
    // silently won, so the sidebar has always been sticky; the dead `relative` is
    // removed rather than the live one, keeping behaviour identical.
    position: 'sticky',
    top: 0,
    overflow: 'hidden',
  },
  sideTop: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '20px 16px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  sideDot: {
    width: '9px', height: '9px', borderRadius: '50%',
    background: '#fff', boxShadow: '0 0 12px rgba(255,255,255,0.9)',
    display: 'inline-block', flexShrink: 0,
  },
  sideBrand: { fontSize: '16px', fontWeight: '800', color: '#fff', letterSpacing: '-0.4px', flex: 1, whiteSpace: 'nowrap' },
  collapseBtn: {
    background: 'transparent', border: 'none', color: '#374151', cursor: 'pointer',
    fontSize: '16px', padding: '4px', flexShrink: 0,
  },
  sideLabel: {
    fontSize: '9px', letterSpacing: '2.5px', color: '#1F2937', fontWeight: '700',
    padding: '16px 16px 8px', whiteSpace: 'nowrap', overflow: 'hidden',
  },
  sideItem: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '10px 12px', margin: '2px 8px',
    borderRadius: '12px', cursor: 'pointer', fontFamily: 'inherit',
    transition: 'all 0.2s ease',
  },
  sideEmoji: { fontSize: '18px', flexShrink: 0, width: '24px', textAlign: 'center' },
  sideItemName: { fontSize: '13px', fontWeight: '600', color: '#D1D5DB', whiteSpace: 'nowrap' },
  sideItemSub: { fontSize: '10px', color: '#374151', marginTop: '1px' },
  sideActiveDot: { width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0 },
  sideBottom: { marginTop: 'auto', padding: '16px', borderTop: '1px solid rgba(255,255,255,0.06)' },
  logoutBtn: {
    display: 'flex', alignItems: 'center', gap: '8px',
    background: 'transparent', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '10px', padding: '10px 12px', cursor: 'pointer',
    color: '#4B5563', fontFamily: 'inherit', fontSize: '13px', width: '100%',
    transition: 'color 0.2s',
  },

  /* ── Main ── */
  main: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative', zIndex: 1 },

  topNav: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '20px 28px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    background: 'rgba(255,255,255,0.02)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    position: 'sticky', top: 0, zIndex: 5,
  },
  navTitle: { fontSize: '18px', fontWeight: '700', color: '#fff', letterSpacing: '-0.4px' },
  navSub: { fontSize: '12px', color: '#374151', marginTop: '2px' },
  navRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  clockPill: {
    display: 'flex', alignItems: 'center', gap: '8px',
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)',
    borderRadius: '20px', padding: '7px 16px',
    fontSize: '13px', fontWeight: '600', color: '#9CA3AF',
    letterSpacing: '0.5px', fontVariantNumeric: 'tabular-nums',
  },
  liveDot: {
    width: '6px', height: '6px', borderRadius: '50%',
    background: '#4ADE80', boxShadow: '0 0 6px #4ADE80',
    display: 'inline-block', flexShrink: 0,
  },
  refreshBtn: {
    display: 'flex', alignItems: 'center', gap: '6px',
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)',
    borderRadius: '20px', padding: '7px 16px',
    fontSize: '15px', color: '#6B7280', cursor: 'pointer', fontFamily: 'inherit',
    transition: 'color 0.2s',
  },
  refreshTime: { fontSize: '11px', color: '#374151', whiteSpace: 'nowrap' },

  content: { padding: '24px 28px', overflowY: 'auto', flex: 1 },

  /* ── KPI Row ── */
  kpiRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '14px',
    marginBottom: '28px',
  },
  kpiCard: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid',
    borderRadius: '20px',
    padding: '20px',
    position: 'relative',
    overflow: 'hidden',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
  },
  kpiAccentLine: { position: 'absolute', top: 0, left: '15%', right: '15%', height: '2px', borderRadius: '0 0 4px 4px' },
  kpiEmoji: { fontSize: '22px', marginBottom: '12px' },
  kpiValue: { fontSize: '28px', fontWeight: '700', letterSpacing: '-0.8px', lineHeight: 1 },
  kpiLabel: { fontSize: '13px', fontWeight: '600', color: '#9CA3AF', marginTop: '4px' },
  kpiUnit: { fontSize: '11px', color: '#374151', marginTop: '2px' },

  /* ── Sections ── */
  section: { marginBottom: '28px' },
  sectionHead: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: '14px',
  },
  sectionTitle: { fontSize: '16px', fontWeight: '700', color: '#E5E7EB', letterSpacing: '-0.3px' },
  sectionCount: { fontSize: '12px', color: '#374151', fontWeight: '500' },

  /* ── Hotel Grid ── */
  hotelGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '14px',
  },
  hotelCard: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: '20px',
    padding: '18px',
    position: 'relative',
    overflow: 'hidden',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
  },
  hotelStrip: { position: 'absolute', top: 0, left: 0, right: 0, height: '2px' },
  hotelHead: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' },
  hotelAvatar: {
    width: '38px', height: '38px', borderRadius: '10px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '11px', fontWeight: '800', letterSpacing: '0.5px', flexShrink: 0,
  },
  hotelName: { fontSize: '13px', fontWeight: '700', color: '#E5E7EB', lineHeight: 1.2 },
  hotelMeta: { fontSize: '11px', color: '#374151', marginTop: '2px' },
  statusPip: { width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0, marginLeft: 'auto' },
  hotelStats: { display: 'flex', gap: '0', marginBottom: '12px' },
  hotelStat: { flex: 1, textAlign: 'center' },
  hotelStatVal: { fontSize: '18px', fontWeight: '700', letterSpacing: '-0.5px', lineHeight: 1 },
  hotelStatLabel: { fontSize: '10px', color: '#374151', marginTop: '3px', letterSpacing: '0.5px' },
  hotelStatDivider: { width: '1px', background: 'rgba(255,255,255,0.06)', alignSelf: 'stretch' },
  barTrack: { height: '3px', background: 'rgba(255,255,255,0.06)', borderRadius: '4px', marginBottom: '10px', overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: '4px', minWidth: '4px' },
  hotelLast: { fontSize: '11px', color: '#374151', textAlign: 'center' },
  walletBtn: {
    width: '100%', marginTop: '4px',
    background: 'rgba(74,222,128,0.12)', color: '#4ADE80',
    border: 'none', borderRadius: '10px', padding: '9px 12px',
    fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit',
  },
  inviteBox: { marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '6px' },
  inviteInput: {
    width: '100%', boxSizing: 'border-box',
    background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '10px', padding: '9px 12px', color: '#E5E7EB',
    fontSize: '12px', fontFamily: 'inherit', outline: 'none',
  },
  inviteResult: {
    marginTop: '14px', background: 'rgba(74,222,128,0.07)',
    border: '1px solid rgba(74,222,128,0.3)', borderRadius: '16px', padding: '18px 20px',
  },
  inviteResultTitle: { fontSize: '14px', fontWeight: '700', color: '#4ADE80', marginBottom: '8px' },
  inviteResultBody: { fontSize: '12.5px', color: '#9CA3AF', lineHeight: 1.6, marginBottom: '12px' },
  inviteResultPass: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: '16px', fontWeight: '700', color: '#E5E7EB', letterSpacing: '0.5px',
    background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '10px', padding: '12px 14px', marginBottom: '12px', wordBreak: 'break-all',
  },
  inviteResultDismiss: {
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '10px', padding: '8px 18px', color: '#9CA3AF',
    fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
  },

  /* ── Feed ── */
  feedTable: {
    background: 'rgba(255,255,255,0.025)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: '18px',
    overflow: 'hidden',
  },
  feedHeader: {
    display: 'flex',
    padding: '10px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    background: 'rgba(255,255,255,0.02)',
  },
  feedHeaderCell: {
    flex: 1, fontSize: '10px', letterSpacing: '2px',
    color: '#374151', fontWeight: '700',
  },
  feedRow: {
    display: 'flex', alignItems: 'center',
    padding: '11px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    transition: 'background 0.15s',
  },
  feedCell: { flex: 1, display: 'flex', alignItems: 'center', minWidth: 0 },
  feedTime: { fontSize: '12px', color: '#6B7280', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  hotelBadge: {
    fontSize: '10px', fontWeight: '800', letterSpacing: '0.5px',
    border: '1px solid', borderRadius: '6px', padding: '2px 7px',
  },
  feedRoom: { fontSize: '12px', color: '#9CA3AF', whiteSpace: 'nowrap' },
  feedItems: {
    fontSize: '12px', color: '#6B7280',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    maxWidth: '100%',
  },
  feedMuted: { fontSize: '12px', color: '#374151' },
  feedAmount: { fontSize: '13px', fontWeight: '700', color: '#E5E7EB', fontVariantNumeric: 'tabular-nums' },
  statusBadge: {
    fontSize: '11px', fontWeight: '600', borderRadius: '8px',
    padding: '3px 10px', whiteSpace: 'nowrap',
  },
  emptyState: {
    textAlign: 'center', padding: '40px',
    fontSize: '14px', color: '#374151',
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '18px',
  },

  /* ── Restaurant Access ── */
  accessCard: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: '20px',
    padding: '18px',
    display: 'flex', flexDirection: 'column', gap: '6px',
    backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
  },
  accessName: { fontSize: '15px', fontWeight: '700', color: '#fff' },
  accessMeta: { fontSize: '12px', color: '#6B7280' },
  accessMetaDim: { fontSize: '11px', color: '#4B5563', marginBottom: '6px' },
  accessBtnRow: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: 'auto' },
  accessBtnPrimary: {
    background: '#fff', color: '#050507', border: 'none', borderRadius: '11px',
    padding: '9px 14px', fontSize: '12px', fontWeight: '700',
    fontFamily: 'inherit', cursor: 'pointer',
  },
  accessBtnGhost: {
    background: 'transparent', color: '#9CA3AF',
    border: '1px solid rgba(255,255,255,0.12)', borderRadius: '11px',
    padding: '9px 14px', fontSize: '12px', fontWeight: '600',
    fontFamily: 'inherit', cursor: 'pointer',
  },
  accessBtnDanger: {
    background: 'rgba(239,68,68,0.16)', color: '#FCA5A5',
    border: '1px solid rgba(239,68,68,0.35)', borderRadius: '11px',
    padding: '9px 14px', fontSize: '12px', fontWeight: '700',
    fontFamily: 'inherit', cursor: 'pointer',
  },
  accessInput: {
    flex: 1, minWidth: '200px', background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.14)', borderRadius: '11px',
    padding: '9px 14px', fontSize: '13px', color: '#E5E7EB',
    fontFamily: 'inherit', outline: 'none',
  },
  accessError: {
    background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
    borderRadius: '12px', padding: '11px 14px',
    fontSize: '13px', color: '#FCA5A5', marginBottom: '14px',
  },
  // Deliberately loud. This is the only time the code is ever visible.
  codeReveal: {
    background: 'rgba(74,222,128,0.07)', border: '1px solid rgba(74,222,128,0.3)',
    borderRadius: '18px', padding: '20px 22px', marginBottom: '16px',
  },
  codeRevealLabel: {
    fontSize: '10px', letterSpacing: '2px', color: '#4ADE80',
    fontWeight: '700', marginBottom: '10px',
  },
  codeRevealValue: {
    fontSize: '32px', fontWeight: '800', color: '#fff',
    letterSpacing: '4px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    marginBottom: '10px', userSelect: 'all',
  },
  codeRevealNote: { fontSize: '12px', lineHeight: 1.6, color: '#9CA3AF', marginBottom: '14px' },
  codeRevealBtn: {
    background: 'rgba(255,255,255,0.1)', color: '#fff',
    border: '1px solid rgba(255,255,255,0.16)', borderRadius: '11px',
    padding: '9px 16px', fontSize: '12px', fontWeight: '700',
    fontFamily: 'inherit', cursor: 'pointer',
  },

  /* ── Product Suite ── */
  productGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '14px',
  },
  productCard: {
    background: 'rgba(255,255,255,0.025)',
    borderRadius: '18px',
    padding: '20px',
    textAlign: 'center',
  },
  productEmoji: { fontSize: '28px', display: 'block', marginBottom: '10px' },
  productName: { fontSize: '14px', fontWeight: '700', color: '#E5E7EB', marginBottom: '4px' },
  productDesc: { fontSize: '11px', color: '#374151', marginBottom: '12px', lineHeight: 1.4 },
  productStatus: {
    display: 'inline-block', fontSize: '11px', fontWeight: '600',
    borderRadius: '8px', padding: '3px 12px',
  },
};
